import React, { useCallback } from 'react';
import { BasicContact, AnsweringSubscriptionTag } from '@bb/domain/dist/types';

import { PopulatedUser } from 'api/UserController/getProfile';
import {
  ContactDrawerMode,
  ContactDrawerType,
} from '../Router/ContactDrawer/types';
import LayoutContext from './LayoutContext';

export { ContactDrawerMode, ContactDrawerType };

export type ContactDrawerContextType = {
  contactDrawerVisible: boolean;
  openContactDrawer: (
    mode: ContactDrawerMode,
    type: ContactDrawerType,
    id?: string,
    opts?: {
      agent?: PopulatedUser;
      contact?: BasicContact;
      subscriptionTag?: AnsweringSubscriptionTag;
    },
  ) => void;
  closeContactDrawer: (resetState?: boolean) => void;
  contactId: string | undefined;
  contactDrawerType: ContactDrawerType;
  contactDrawerMode?: ContactDrawerMode | undefined;
  contactType?: 'client' | 'supplier';
  coopContact?: BasicContact;
  agentContact?: PopulatedUser;
  agentSubscriptionTag?: AnsweringSubscriptionTag;
  setContactDrawerMode: (mode: ContactDrawerMode) => void;
  setContactType: (type: 'client' | 'supplier') => void;
  reloadToggle: boolean;
  triggerReload: () => void;
};

const ContactDrawerContext = React.createContext<ContactDrawerContextType>(
  {} as any,
);

export const ContactDrawerContextProvider: React.FC<{
  isExternal?: boolean;
}> = ({ children, isExternal }) => {
  const { setChatVisibility } = React.useContext(LayoutContext);
  const [reloadToggle, setReloadToggle] = React.useState(false);
  const [contactDrawerVisible, setContactDrawerVisible] = React.useState(false);
  const [contactId, setContactId] = React.useState('');
  const [contactDrawerType, setContactDrawerType] = React.useState(
    ContactDrawerType.User,
  );
  const [contactDrawerMode, setContactDrawerMode] = React.useState(
    ContactDrawerMode.Profile,
  );
  const [contactType, setContactType] = React.useState<
    'client' | 'supplier' | undefined
  >(undefined);
  const [coopContact, setCoopContact] = React.useState<BasicContact>();
  const [agentContact, setAgentContact] = React.useState<PopulatedUser>();
  const [agentSubscriptionTag, setAgentSubscriptionTag] =
    React.useState<AnsweringSubscriptionTag>();

  const triggerReload = useCallback(() => {
    setReloadToggle(!reloadToggle);
  }, [reloadToggle]);

  const openContactDrawer: ContactDrawerContextType['openContactDrawer'] = (
    mode,
    type,
    id,
    opts,
  ) => {
    const { contact, agent, subscriptionTag } = opts || {};
    setContactId(id || '');
    setContactDrawerType(type);
    setContactDrawerMode(mode);
    setContactDrawerVisible(true);
    setAgentSubscriptionTag(subscriptionTag);
    if (!isExternal) setChatVisibility(false);
    if (contact) setCoopContact(contact);
    if (agent) setAgentContact(agent);
  };

  const closeContactDrawer = (resetState = true) => {
    setContactDrawerVisible(false);
    if (!isExternal) setChatVisibility(true);
    if (resetState) {
      setContactId('');
      setContactDrawerType(ContactDrawerType.User);
      setContactDrawerMode(ContactDrawerMode.Profile);
    }
  };

  return (
    <ContactDrawerContext.Provider
      value={{
        contactDrawerVisible,
        openContactDrawer,
        closeContactDrawer,
        contactId,
        contactDrawerMode,
        contactDrawerType,
        contactType,
        coopContact,
        agentContact,
        agentSubscriptionTag,
        setContactDrawerMode,
        setContactType,
        reloadToggle,
        triggerReload,
      }}
    >
      {children}
    </ContactDrawerContext.Provider>
  );
};

export default ContactDrawerContext;
